import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';

import Loading from 'components/common/loading/Loading';

import { SKILLS } from 'constants/permissions';

import {
  useCreateSkillCategoryMutation,
  useCreateSkillMutation,
  useDeleteSkillCategoryMutation,
  useDeleteSkillMutation,
  useFetchSkillCategoriesQuery,
  useFetchSkillsV1Query,
  useUpdateSkillCategoryMutation,
  useUpdateSkillMutation,
} from 'hooks/query/useSkillQueries';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import * as toast from 'utils/toast';

import { SkillSetV1, EditSkill, EditSkillCategory } from './components';

import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type {
  LegacySkill,
  LegacySkillCategory,
  SkillCategoryFormValues,
  SkillFormValues,
  SkillsV1Props,
} from './types';

type SkillListResponse = {
  data: LegacySkill[];
};

type SkillCategoryListResponse = {
  data: LegacySkillCategory[];
};

const toFormSkillValues = (skill: LegacySkill): SkillFormValues => ({
  id: skill.id == null ? undefined : Number(skill.id),
  name: skill.name,
  description: skill.description ?? '',
  skillCategory: skill.skillCategory,
});

const Skills: React.FC<SkillsV1Props> = ({ user }) => {
  const permissions = useUserPermissions() as PermissionMap;

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSkill, setActiveSkill] = useState<SkillFormValues | null>(null);
  const [activeSkillCategory, setActiveSkillCategory] = useState<SkillCategoryFormValues | null>(null);
  const [showEditSkill, setShowEditSkill] = useState(false);
  const [showEditSkillCategory, setShowEditSkillCategory] = useState(false);

  const { data: skillCategoriesResponse, isLoading: isLoadingCategories } =
    useFetchSkillCategoriesQuery<SkillCategoryListResponse>({ size: 10000 });
  const { data: skillsResponse, isLoading: isLoadingSkills } = useFetchSkillsV1Query<SkillListResponse>();

  const { mutateAsync: createSkill, isPending: isCreatingSkill } = useCreateSkillMutation();
  const { mutateAsync: updateSkill, isPending: isUpdatingSkill } = useUpdateSkillMutation();
  const { mutateAsync: deleteSkill, isPending: isDeletingSkill } = useDeleteSkillMutation();
  const { mutateAsync: createSkillCategory, isPending: isCreatingCategory } = useCreateSkillCategoryMutation();
  const { mutateAsync: updateSkillCategory, isPending: isUpdatingCategory } = useUpdateSkillCategoryMutation();
  const { mutateAsync: deleteSkillCategory, isPending: isDeletingCategory } = useDeleteSkillCategoryMutation();

  const skillCategories = skillCategoriesResponse?.data ?? [];
  const skills = skillsResponse?.data ?? [];
  const isLoading = isLoadingCategories || isLoadingSkills;
  const isSaving = isCreatingSkill || isUpdatingSkill || isCreatingCategory || isUpdatingCategory;
  const isDeleting = isDeletingSkill || isDeletingCategory;

  useEffect(() => {
    window.document.title = 'Skills | Vyaguta';
  }, []);

  const handleSkillAdd = (skillCategory: LegacySkillCategory) => {
    if (activeSkill && !activeSkill.id && activeSkill.skillCategory?.id === skillCategory.id) {
      handleSkillCancel();

      return;
    }

    setActiveSkill({
      name: '',
      description: '',
      skillCategory,
    });
    setShowEditSkill(true);
  };

  const handleSkillClick = (selectedSkill: LegacySkill) => {
    if (showEditSkillCategory) {
      handleSkillCategoryCancel();
    }

    const nextActiveSkill = activeSkill?.id === Number(selectedSkill.id) ? null : toFormSkillValues(selectedSkill);

    setActiveSkill(nextActiveSkill);
    setShowEditSkill(Boolean(nextActiveSkill));
  };

  const handleSkillCancel = () => {
    setShowEditSkill(false);
    setActiveSkill(null);
  };

  const handleSkillSave = async (skill: SkillFormValues, id: number | null = null) => {
    if (!skill.skillCategory) {
      toast.error({ title: 'Error', message: 'Please enter the skill category' });

      return;
    }

    try {
      const skillPayload = {
        name: skill.name,
        description: skill.description ?? null,
        skillCategory: {
          id: skill.skillCategory.id ?? 0,
          name: skill.skillCategory.name,
        },
      };

      if (id) {
        await updateSkill({ skill: skillPayload, id });
      } else {
        await createSkill(skillPayload);
      }

      setActiveSkill(null);
      setShowEditSkill(false);

      toast.success({
        title: 'Success',
        message: `Skill has been ${id ? 'updated' : 'created'} successfully.`,
      });
    } catch {
      // Error is already handled in mutation onError.
    }
  };

  const handleSkillDelete = async (skill: SkillFormValues) => {
    if (!skill.id) {
      return;
    }

    try {
      await deleteSkill(skill.id);
      setActiveSkill(null);
      setShowEditSkill(false);

      toast.success({
        title: 'Success',
        message: 'Skill has been deleted successfully.',
      });
    } catch {
      // Error is already handled in mutation onError.
    }
  };

  const handleSkillCategoryCancel = () => {
    setShowEditSkillCategory(false);
    setActiveSkillCategory(null);
  };

  const handleSkillCategorySave = async (skillCategory: SkillCategoryFormValues, id: number | null = null) => {
    try {
      const skillCategoryPayload = {
        name: skillCategory.name,
        description: skillCategory.description ?? null,
      };

      if (id) {
        await updateSkillCategory({ category: skillCategoryPayload, id });
      } else {
        await createSkillCategory(skillCategoryPayload);
      }

      toast.success({
        title: 'Success',
        message: `Skill Category has been ${id ? 'updated' : 'created'} successfully.`,
      });

      setShowEditSkillCategory(false);
    } catch {
      // Error is already handled in mutation onError.
    }
  };

  const handleSkillCategoryDelete = async (id: number) => {
    try {
      await deleteSkillCategory(id);

      toast.success({
        title: 'Success',
        message: 'Skill Category has been deleted successfully.',
      });
    } catch {
      // Error is already handled in mutation onError.
    }
  };

  const handleSearchChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(value);
  };

  const handleSkillCategorySelect = (category: LegacySkillCategory) => {
    if (showEditSkill) {
      handleSkillCancel();
    }

    if (showEditSkillCategory && activeSkillCategory?.id === category.id) {
      handleSkillCategoryCancel();
    } else {
      setActiveSkillCategory(category);
      setShowEditSkillCategory(true);
    }
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Skills</div>
          <div className="table-subtitle">{`Showing ${skills.length} Results`}</div>
        </div>

        <div className="d-flex flex-row">
          <div className="search-wrapper skills-search d-flex flex-row align-items-center">
            <MdSearch className="search-icon" />
            <input
              className="search-bar skills-search-input"
              value={searchQuery}
              type="text"
              onChange={handleSearchChange}
              placeholder="Search by keywords"
            />
          </div>
        </div>
      </div>
      <div className={classNames({ loading__container: isLoading })}>
        <div className="full-scope-card__content d-flex position-relative">
          {isLoading ? (
            <Loading className="mt-120" />
          ) : (
            <>
              <div className="skill-set-area">
                <SkillSetV1
                  skillCategories={skillCategories}
                  searchQuery={searchQuery}
                  skills={skills}
                  activeSkill={activeSkill}
                  onSkillCategoryClick={handleSkillCategorySelect}
                  onSkillClick={handleSkillClick}
                  onSkillAdd={handleSkillAdd}
                  permissions={permissions}
                  isHr={user.isHr}
                />
              </div>
              {permissions[SKILLS.UPDATE] && (
                <div className="skill-edit-area">
                  {showEditSkillCategory && (
                    <EditSkillCategory
                      skillCategory={activeSkillCategory}
                      isHr={user.isHr}
                      handleSave={handleSkillCategorySave}
                      handleDelete={handleSkillCategoryDelete}
                      handleCancel={handleSkillCategoryCancel}
                      saving={isSaving}
                      deleting={isDeleting}
                    />
                  )}
                  {showEditSkill && !(activeSkillCategory && !activeSkillCategory.id) && (
                    <EditSkill
                      skill={activeSkill}
                      isHr={user.isHr}
                      skillCategories={skillCategories}
                      handleSave={handleSkillSave}
                      handleDelete={handleSkillDelete}
                      handleCancel={handleSkillCancel}
                      saving={isSaving}
                      deleting={isDeleting}
                    />
                  )}
                  {!showEditSkill && !showEditSkillCategory && (
                    <div className="empty-skill-set">Click on any of the skill to view details here</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Skills;
